import React, { useCallback, useEffect, useState } from 'react';
import api from '../../utils/api';

/*
 * "Set Pricing By Party Count" — a port of scale-portal's
 * components/template/Account/components/PricingModel/*.
 *
 * Three things from the original that are deliberately NOT carried across:
 *
 *  - `booking_type` is never written by any Scale UI; the production rows that
 *    have it were tagged by hand in the database. So it is not exposed here
 *    either, and the grouped list view it drives is left alone rather than
 *    half-built.
 *  - Scale's own empty state is unreachable (the flat branch returns first), so
 *    a mediator with no rules sees a blank area. This renders the message Scale
 *    wrote but never shows.
 *  - Scale's tier update filters on the row id alone. Ours always filters on the
 *    mediator too, so a rule belonging to another mediator cannot be reached.
 */

const TIER_OPTIONS = [3, 4, 5, 6, 7, 8, 9, 10];
const BILLING_TYPES = [
  { label: 'Hourly Rate', value: 'hourly' },
  { label: 'Fixed Rate', value: 'fixed' },
];
const ALLOCATIONS = [
  {
    value: 'equally_split',
    label: 'Equally Split Between All Parties',
    tooltip: 'Each named party pays an equal share of the total invoice. Example: If there are 5 total parties, each pays 20%.',
  },
  {
    value: '50_percent',
    label: '50% Split by Side (Plaintiff / Defendant)',
    tooltip: 'Each side pays 50% of the invoice. That 50% is then evenly split among the parties on that side. Example: If there are 2 plaintiffs and 3 defendants, each plaintiff pays 25%, and each defendant pays ~16.7%.',
  },
];

const money = (v) => {
  if (v === null || v === undefined || v === '') return '';
  const n = parseInt(String(v).replace(/[^0-9]/g, ''), 10);
  if (isNaN(n)) return '';
  return new Intl.NumberFormat('en-US', { style: 'currency', currency: 'USD', minimumFractionDigits: 0 }).format(n);
};
const digits = (v) => String(v ?? '').replace(/[^0-9]/g, '');
const toNum = (v) => (v === '' || v === null || v === undefined ? null : parseInt(v, 10));

// Scale's own control classes, ported into src/styles/index.css.
const control = 'input input-md';
const selectCls = 'select-control';
const cstyle = { fontFamily: 'Inter, sans-serif', color: '#0e1e20' };
const lbl = 'text-black pb-2 flex items-center text-sm';

export default function PricingTiersSection({ mediatorId, notify, allocation, onAllocationChange }) {
  const [tiers, setTiers] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showForm, setShowForm] = useState(false);
  const [form, setForm] = useState({ tier: '', pricing_type: '', half_day_rate: '', full_day_rate: '', hourly_rate: '', deposit: '' });
  const [errors, setErrors] = useState({});
  const [busy, setBusy] = useState(false);

  const load = useCallback(async () => {
    if (!mediatorId) { setLoading(false); return; }
    try {
      setLoading(true);
      const res = await api.get(`/mediators/${mediatorId}/pricing-tiers`);
      setTiers(res.data?.data || []);
    } catch (err) {
      notify?.({ type: 'error', message: err.response?.data?.message || 'Failed to load pricing rules' });
    } finally {
      setLoading(false);
    }
  }, [mediatorId, notify]);

  useEffect(() => { load(); }, [load]);

  const usedTiers = new Set(tiers.map((t) => t.tier));

  const validate = () => {
    const e = {};
    if (!form.tier) e.tier = 'Party Tier is required';
    if (!form.pricing_type) e.pricing_type = 'Billing Type is required';
    if (form.pricing_type === 'fixed') {
      if (!form.half_day_rate) e.half_day_rate = 'Half-day rate is required';
      if (!form.full_day_rate) e.full_day_rate = 'Full-day rate is required';
    }
    if (form.pricing_type === 'hourly') {
      if (!form.hourly_rate) e.hourly_rate = 'Hourly rate is required';
      if (!form.deposit) e.deposit = 'Deposit is required';
    }
    setErrors(e);
    return Object.keys(e).length === 0;
  };

  const create = async () => {
    if (!validate()) return;
    const payload = { tier: Number(form.tier), pricing_type: form.pricing_type };
    if (form.pricing_type === 'fixed') {
      payload.half_day_rate = toNum(form.half_day_rate);
      payload.full_day_rate = toNum(form.full_day_rate);
    } else {
      payload.hourly_rate = toNum(form.hourly_rate);
      payload.deposit = toNum(form.deposit);
    }
    try {
      setBusy(true);
      await api.post(`/mediators/${mediatorId}/pricing-tiers`, payload);
      notify?.({ type: 'success', message: 'Pricing Tier Created Successfully!' });
      setForm({ tier: '', pricing_type: '', half_day_rate: '', full_day_rate: '', hourly_rate: '', deposit: '' });
      setShowForm(false);
      load();
    } catch (err) {
      notify?.({ type: 'error', message: err.response?.data?.message || 'Failed to create pricing rule' });
    } finally {
      setBusy(false);
    }
  };

  const patch = async (tierId, body, successMessage) => {
    try {
      await api.put(`/mediators/${mediatorId}/pricing-tiers/${tierId}`, body);
      notify?.({ type: 'success', message: successMessage });
      load();
    } catch (err) {
      notify?.({ type: 'error', message: err.response?.data?.message || 'Failed to update rates' });
      load();
    }
  };

  const remove = async (tierId) => {
    try {
      await api.delete(`/mediators/${mediatorId}/pricing-tiers/${tierId}`);
      notify?.({ type: 'success', message: 'Tier deleted successfully!' });
      load();
    } catch (err) {
      notify?.({ type: 'error', message: err.response?.data?.message || 'Failed to delete tier' });
    }
  };

  return (
    <div className="col-span-2">
      <div className="flex flex-col gap-y-1 py-2">
        <h3 className="text-xl font-semibold text-[#0e1e20]" style={{ fontFamily: "'Inter Tight', sans-serif" }}>Set Pricing By Party Count</h3>
        <p className="text-sm font-normal text-[#5b5a64]" style={{ fontFamily: 'Inter, sans-serif' }}>Customized rates by number of parties.</p>
      </div>

      <div className="bg-white p-8 w-full rounded-md shadow">
        {/* Header strip: the two allocation "tabs" on the left, Create New Rule
            on the right. They look like tabs but are a two-state setting — they
            do not filter the list. That is Scale's behaviour, kept. */}
        <div className="flex items-center justify-between flex-wrap gap-3 border-b border-gray-300 pb-3">
          <div className="flex items-center gap-6 flex-wrap">
            {ALLOCATIONS.map((a) => {
              const selected = allocation === a.value;
              return (
                <span
                  key={a.value}
                  role="button"
                  tabIndex={0}
                  title={a.tooltip}
                  onClick={() => onAllocationChange(a.value)}
                  onKeyDown={(e) => { if (e.key === 'Enter' || e.key === ' ') onAllocationChange(a.value); }}
                  className={`pb-2 cursor-pointer text-sm ${selected ? 'border-b-[3px] border-[#115E59] text-[#115E59] font-semibold' : 'text-[#5b5a64]'}`}
                  style={{ fontFamily: 'Inter, sans-serif' }}
                >
                  <input type="radio" readOnly checked={selected} className="mr-2 align-middle" style={{ accentColor: '#115e59' }} />
                  {a.label}
                </span>
              );
            })}
          </div>
          <button
            type="button"
            onClick={() => setShowForm((v) => !v)}
            className="button button-solid button-sm"
            style={{ fontFamily: 'Inter, sans-serif', background: '#115e59' }}
          >
            Create New Rule
          </button>
        </div>

        {showForm && (
          <div className="mt-6 border border-gray-200 rounded-md p-4">
            <h2 className="text-base font-semibold text-[#0e1e20] mb-4" style={{ fontFamily: "'Inter Tight', sans-serif" }}>Set Pricing By Party Count</h2>
            <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
              <div>
                <label className={lbl} style={cstyle}>Number of Parties</label>
                <select className={selectCls} style={cstyle} value={form.tier} onChange={(e) => setForm((p) => ({ ...p, tier: e.target.value }))}>
                  <option value="">Select...</option>
                  {TIER_OPTIONS.map((t) => (
                    <option key={t} value={t} disabled={usedTiers.has(t)}>{t}+</option>
                  ))}
                </select>
                {errors.tier && <span className="text-[11px] text-red-600">{errors.tier}</span>}
              </div>
              <div>
                <label className={lbl} style={cstyle}>Billing Type</label>
                <select className={selectCls} style={cstyle} value={form.pricing_type} onChange={(e) => setForm((p) => ({ ...p, pricing_type: e.target.value }))}>
                  <option value="">Select...</option>
                  {BILLING_TYPES.map((o) => <option key={o.value} value={o.value}>{o.label}</option>)}
                </select>
                {errors.pricing_type && <span className="text-[11px] text-red-600">{errors.pricing_type}</span>}
              </div>

              {form.pricing_type === 'fixed' && (
                <>
                  <div>
                    <label className={lbl} style={cstyle}>Half-day Rate</label>
                    <input className={control} style={cstyle} placeholder="Enter Half-day amount"
                      value={money(form.half_day_rate)}
                      onChange={(e) => setForm((p) => ({ ...p, half_day_rate: digits(e.target.value) }))} />
                    {errors.half_day_rate && <span className="text-[11px] text-red-600">{errors.half_day_rate}</span>}
                  </div>
                  <div>
                    <label className={lbl} style={cstyle}>Full-day Rate</label>
                    <input className={control} style={cstyle} placeholder="Enter Full-day amount"
                      value={money(form.full_day_rate)}
                      onChange={(e) => setForm((p) => ({ ...p, full_day_rate: digits(e.target.value) }))} />
                    {errors.full_day_rate && <span className="text-[11px] text-red-600">{errors.full_day_rate}</span>}
                  </div>
                </>
              )}
              {form.pricing_type === 'hourly' && (
                <>
                  <div>
                    <label className={lbl} style={cstyle}>Hourly Rate</label>
                    <input className={control} style={cstyle} placeholder="Enter Hourly Rate"
                      value={money(form.hourly_rate)}
                      onChange={(e) => setForm((p) => ({ ...p, hourly_rate: digits(e.target.value) }))} />
                    {errors.hourly_rate && <span className="text-[11px] text-red-600">{errors.hourly_rate}</span>}
                  </div>
                  <div>
                    <label className={lbl} style={cstyle}>Deposit</label>
                    <input className={control} style={cstyle} placeholder="Enter Deposit"
                      value={money(form.deposit)}
                      onChange={(e) => setForm((p) => ({ ...p, deposit: digits(e.target.value) }))} />
                    {errors.deposit && <span className="text-[11px] text-red-600">{errors.deposit}</span>}
                  </div>
                </>
              )}
            </div>
            <div className="flex items-center justify-end gap-2 mt-4">
              <button type="button" onClick={() => { setShowForm(false); setErrors({}); }}
                className="button button-default button-sm"
                style={{ fontFamily: 'Inter, sans-serif' }}>Cancel</button>
              <button type="button" onClick={create} disabled={busy}
                className="button button-solid button-sm"
                style={{ fontFamily: 'Inter, sans-serif', background: busy ? '#e5e5e5' : '#115e59', color: busy ? '#9ca3af' : '#fff' }}>
                {busy ? 'Saving…' : 'Add New Rule'}
              </button>
            </div>
          </div>
        )}

        <div className="mt-6">
          {loading ? (
            <div className="flex items-center justify-center py-8">
              <div className="w-6 h-6 border-2 border-[#115e59] border-t-transparent rounded-full animate-spin" />
            </div>
          ) : tiers.length === 0 ? (
            <div className="py-6">
              <p className="text-sm font-semibold text-[#0e1e20]" style={{ fontFamily: 'Inter, sans-serif' }}>No party-count rules yet</p>
              <p className="text-xs text-gray-600 mt-1" style={{ fontFamily: 'Inter, sans-serif' }}>
                Your default rates apply to every case. Use &ldquo;Create New Rule&rdquo; above to charge
                customized rates based on the number of parties in a case.
              </p>
            </div>
          ) : (
            <div className="flex flex-col gap-3">
              {tiers.map((t) => (
                <TierRow key={t.id} tier={t} onPatch={patch} onRemove={remove} />
              ))}
            </div>
          )}
        </div>
      </div>
    </div>
  );
}

function TierRow({ tier, onPatch, onRemove }) {
  const [local, setLocal] = useState({
    half_day_rate: tier.half_day_rate ?? '',
    full_day_rate: tier.full_day_rate ?? '',
    hourly_rate: tier.hourly_rate ?? '',
    deposit: tier.deposit ?? '',
  });
  const isHourly = tier.pricing_type === 'hourly';
  const blurSave = (field) => {
    const sent = toNum(digits(local[field]));
    const original = tier[field] ?? null;
    if (sent === original) return;
    onPatch(tier.id, { [field]: sent }, 'Rates updated successfully!');
  };

  return (
    <div className="border border-gray-200 rounded-md p-4 grid grid-cols-2 md:grid-cols-5 gap-4 items-end">
      <div>
        <label className={lbl} style={cstyle}>Number of Parties</label>
        <input className={`${control} input-disabled`} style={cstyle} value={`${tier.tier}+`} readOnly disabled />
      </div>
      <div>
        <label className={lbl} style={cstyle}>Billing Type</label>
        <select className={selectCls} style={cstyle} value={tier.pricing_type || ''}
          onChange={(e) => onPatch(tier.id, { pricing_type: e.target.value }, 'Billing type updated successfully!')}>
          {BILLING_TYPES.map((o) => <option key={o.value} value={o.value}>{o.label}</option>)}
        </select>
      </div>
      {isHourly ? (
        <>
          <div>
            <label className={lbl} style={cstyle}>Hourly Rate</label>
            <input className={control} style={cstyle} value={money(local.hourly_rate)}
              onChange={(e) => setLocal((p) => ({ ...p, hourly_rate: digits(e.target.value) }))}
              onBlur={() => blurSave('hourly_rate')} />
          </div>
          <div>
            <label className={lbl} style={cstyle}>Deposit</label>
            <input className={control} style={cstyle} value={money(local.deposit)}
              onChange={(e) => setLocal((p) => ({ ...p, deposit: digits(e.target.value) }))}
              onBlur={() => blurSave('deposit')} />
          </div>
        </>
      ) : (
        <>
          <div>
            <label className={lbl} style={cstyle}>Half-day Rate</label>
            <input className={control} style={cstyle} value={money(local.half_day_rate)}
              onChange={(e) => setLocal((p) => ({ ...p, half_day_rate: digits(e.target.value) }))}
              onBlur={() => blurSave('half_day_rate')} />
          </div>
          <div>
            <label className={lbl} style={cstyle}>Full-day Rate</label>
            <input className={control} style={cstyle} value={money(local.full_day_rate)}
              onChange={(e) => setLocal((p) => ({ ...p, full_day_rate: digits(e.target.value) }))}
              onBlur={() => blurSave('full_day_rate')} />
          </div>
        </>
      )}
      <div className="flex justify-end">
        <button type="button" onClick={() => onRemove(tier.id)}
          className="text-xs text-red-500 hover:text-red-600 font-medium"
          style={{ fontFamily: 'Inter, sans-serif' }}>Remove</button>
      </div>
    </div>
  );
}
